import { createClient } from '@wix/sdk/client';
import { cartV2 as cart, currentCartV2 as currentCart } from '@wix/ecom';
import type { Cart, ICurrentCartService } from '../current-cart-service/types';
import { EcomPlatformViewerScriptContext } from '../../viewer-script/types';
import { getSDKAuthAndHost } from '../../utils/getSDKAuthAndHost';
import { DEADLINE_HEADER_NAME } from '../../constants';
import { getTimeoutMs } from '../../utils/utils';

export class CartService {
  private readonly cartApi;
  private readonly cartApiWithExtendedDeadline;
  private readonly currentCartService: ICurrentCartService;

  constructor({ context }: { context: EcomPlatformViewerScriptContext }) {
    this.currentCartService = context.currentCartService;
    this.cartApi = createClient({
      ...getSDKAuthAndHost(context.siteStore),
      modules: {
        cart: {
          getCart: cart.getCart,
          calculateCart: cart.calculateCart,
          updateCart: cart.updateCart,
          removeGiftCard: cart.removeGiftCard,
          addGiftCard: cart.addGiftCard,
          addCoupon: cart.addCoupon,
          removeCoupon: cart.removeCoupon,
          removeLineItems: cart.removeLineItems,
          setDeliveryMethod: cart.setDeliveryMethod,
          removeDeliveryMethod: cart.removeDeliveryMethod,
          refreshCart: cart.refreshCart,
          placeOrder: cart.placeOrder,
          updateFormSubmissions: cart.updateFormSubmissions,
        },
      },
    }).cart;
    this.cartApiWithExtendedDeadline = createClient({
      ...getSDKAuthAndHost(context.siteStore, {
        additionalHeaders: {
          [DEADLINE_HEADER_NAME]: getTimeoutMs(30).toString(),
        },
      }),
      modules: { cart: { placeOrder: cart.placeOrder } },
    }).cart;
  }

  private async shouldUseCurrentCartService(cartId: string) {
    return (
      (await this.currentCartService.getCurrentCartIdFromCache()) === cartId
    );
  }

  public async getCart(
    cartId: string,
    forceById: boolean = false,
  ): Promise<Cart> {
    if ((await this.shouldUseCurrentCartService(cartId)) && !forceById) {
      return this.currentCartService.getCurrentCart();
    }
    return this.cartApi.getCart(cartId);
  }

  public async calculateCart(
    cartId: string,
  ): Promise<currentCart.CalculateCurrentCartResponse> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.calculateCurrentCart();
    }
    return this.cartApi.calculateCart(cartId);
  }

  public async updateCart(
    cartId: string,
    updateCart: cart.UpdateCart,
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.updateCurrentCart(updateCart);
    }
    return this.cartApi.updateCart(cartId, updateCart);
  }

  public async removeGiftCard(
    cartId: string,
    giftCardId: string,
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.removeGiftCardFromCurrentCart({
        giftCardId,
      });
    }
    return (await this.cartApi.removeGiftCard(cartId, giftCardId)).cart!;
  }

  public async addGiftCard(
    cartId: string,
    giftCardInput: cart.GiftCardInput,
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.addGiftCardToCurrentCart({
        giftCardInput,
      });
    }
    return (await this.cartApi.addGiftCard(cartId, giftCardInput)).cart!;
  }

  public async removeCoupon(cartId: string, couponId: string): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.removeCouponFromCurrentCart({ couponId });
    }
    return (await this.cartApi.removeCoupon(cartId, couponId)).cart!;
  }

  public async addCoupon(
    cartId: string,
    couponInput: cart.CouponInput,
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.addCouponToCurrentCart({
        coupon: couponInput,
      });
    }
    return (await this.cartApi.addCoupon(cartId, couponInput)).cart!;
  }

  public async removeLineItems(
    cartId: string,
    lineItemIds: string[],
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.removeLineItemsFromCurrentCart({
        lineItemIds,
      });
    }
    return (await this.cartApi.removeLineItems(cartId, lineItemIds)).cart!;
  }

  public async setDeliveryMethod(
    cartId: string,
    deliveryMethod: cart.DeliveryMethodInput,
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.setDeliveryMethodForCurrentCart({
        deliveryMethod,
      });
    }
    return (await this.cartApi.setDeliveryMethod(cartId, deliveryMethod)).cart!;
  }

  public async removeDeliveryMethod(cartId: string): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.removeDeliveryMethodFromCurrentCart();
    }
    return (await this.cartApi.removeDeliveryMethod(cartId)).cart!;
  }

  public async refreshCart(cartId: string): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.refreshCurrentCart();
    }
    return (await this.cartApi.refreshCart(cartId)).cart!;
  }

  public placeOrder(cartId: string, placeOrderOptions: cart.PlaceOrderOptions) {
    return this.cartApiWithExtendedDeadline.placeOrder(
      cartId,
      placeOrderOptions,
    );
  }

  public async updateFormSubmissions(
    cartId: string,
    options: cart.UpdateFormSubmissionsOptions,
  ): Promise<Cart> {
    if (await this.shouldUseCurrentCartService(cartId)) {
      return this.currentCartService.updateFormSubmissionsInCurrentCart(
        options,
      );
    }
    return (await this.cartApi.updateFormSubmissions(cartId, options)).cart!;
  }
}
